import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useTranslation } from '@/hooks/useTranslation';
import logo from '@/assets/images/logo-sidebar.png';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError(null);
      setIsLoading(true);
      await authApi.forgotPassword(data.email);
      setSuccess(true);
    } catch (err: any) {
      // Mensagens de erro mais amigáveis
      let errorMessage = err.message || 'Falha ao enviar email de recuperação';
      
      if (errorMessage.includes('User not found') || errorMessage.includes('not found')) {
        errorMessage = 'Email não encontrado. Verifique se você digitou corretamente ou crie uma nova conta.';
      } else if (errorMessage.includes('Too many requests')) {
        errorMessage = 'Muitas tentativas. Por favor, aguarde alguns minutos e tente novamente.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: '#F6F6F8' }}
      >
        <div 
          className="w-full"
          style={{
            maxWidth: '450px',
            backgroundColor: '#FFF',
            borderRadius: '8px',
            padding: '48px 40px',
            boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)'
          }}
        >
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src={logo} 
                alt="FeedBatch Logo" 
                style={{ 
                  height: '120px',
                  width: 'auto'
                }}
              />
            </div>
            <div 
              className="mb-6 p-4 rounded-lg"
              style={{
                backgroundColor: '#E8F5E9',
                border: '1px solid #4CAF50'
              }}
            >
              <p 
                className="text-sm"
                style={{ color: '#2E7D32' }}
              >
                {t('auth.resetEmailSent')}
              </p>
            </div>
            <Link
              to="/login"
              style={{ 
                color: 'var(--wl-primary)',
                fontWeight: 500,
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
            >
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#F6F6F8' }}
    >
      <div 
        className="w-full"
        style={{
          maxWidth: '450px',
          backgroundColor: '#FFF',
          borderRadius: '8px',
          padding: '48px 40px',
          boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)'
        }}
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={logo} 
              alt="FeedBatch Logo" 
              style={{ 
                height: '120px',
                width: 'auto'
              }}
            />
          </div>
          <h2 
            className="text-2xl font-medium mb-2"
            style={{ color: '#22212C', fontWeight: 500 }}
          >
            {t('auth.forgotPasswordTitle')}
          </h2>
          <p 
            className="text-sm"
            style={{ color: 'rgba(50, 71, 92, 0.6)' }}
          >
            {t('auth.forgotPasswordSubtitle')}
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div 
              style={{
                backgroundColor: '#FDEDEF',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #E5384F'
              }}
            >
              <p 
                className="text-sm"
                style={{ color: '#E5384F' }}
              >
                {error}
              </p>
              {error.includes('não encontrado') && (
                <Link
                  to="/register"
                  style={{ 
                    color: 'var(--wl-primary)',
                    fontWeight: 500,
                    textDecoration: 'underline',
                    fontSize: '0.875rem',
                    display: 'block',
                    marginTop: '8px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
                >
                  Criar uma nova conta
                </Link>
              )}
            </div>
          )}

          <Input
            label={t('auth.email')}
            type="email"
            autoComplete="email"
            placeholder="seu@email.com.br"
            error={errors.email?.message}
            {...register('email')}
          />

          <Button type="submit" className="w-full" isLoading={isLoading}>
            {t('auth.sendResetLink')}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              style={{ 
                color: 'var(--wl-primary)',
                fontWeight: 500,
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
            >
              {t('auth.backToLogin')}
            </Link>
          </div>
        </form>

        {/* Links de Privacidade e Termos */}
        <div className="text-center mt-6 flex justify-center gap-3 text-xs" style={{ color: 'rgba(50, 71, 92, 0.6)' }}>
          <a
            href="/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Política de Privacidade
          </a>
          <span>•</span>
          <a
            href="/termos-de-servico"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Termos de Serviço
          </a>
        </div>
      </div>
    </div>
  );
}
